import React, { useRef, useState } from 'react';
import { Form, Button, Badge } from 'react-bootstrap';
import { FloatingField } from '../../Transitions';
import { PinIcon, LoaderIcon } from '../../Icons';

interface MessageInputProps {
  activeConvId: string | null;
  loading: boolean;
  isClosed: boolean;
  onSend: (body: string, file: File | null) => Promise<void>;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  activeConvId,
  loading,
  isClosed,
  onSend,
}) => {
  const [body, setBody] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  // Inline file-validation error, shown in the compose box instead of a blocking alert().
  const [fileError, setFileError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError('File size exceeds 10MB limit.');
      e.target.value = ''; // allow re-selecting the same file after fixing
      return;
    }

    setFileError('');
    setSelectedFile(file);
    e.target.value = ''; // Reset for re-selection
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setFileError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeConvId) return;
    if (!body.trim() && !selectedFile) return;

    setUploading(true);
    try {
      await onSend(body, selectedFile);
      setBody('');
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const canSend = activeConvId && (body.trim() || selectedFile) && !isClosed && !uploading;

  return (
    <div className='p-4'>
      <Form noValidate onSubmit={handleSubmit}>
        {fileError && (
          <div className="text-danger mb-2" role="alert">{fileError}</div>
        )}

        {/* Selected file preview */}
        {selectedFile && (
          <div className="mb-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span>📎</span>
              <span className="text-truncate" style={{ maxWidth: '250px' }}>
                {selectedFile.name}
              </span>
              <Badge bg="secondary" pill>
                {(selectedFile.size / 1024).toFixed(1)} KB
              </Badge>
            </div>
            <Button 
              variant="link" 
              size="sm" 
              className="text-danger p-0"
              onClick={handleClearFile}
              disabled={uploading}
            >
              ✕
            </Button>
          </div>
        )}

        <FloatingField
          type='textarea'
          className='w-100'
          label={selectedFile ? 'Add a message (optional)' : 'Send a message'}
          value={body}
          onChange={(e: any) => setBody(e.target.value)}
          style={{ height: 120 }}
          disabled={!activeConvId || uploading}
        />

        <Form.Group className='position-absolute bottom-0 end-0 mb-5 me-4 d-flex align-items-center gap-2'>
          <input
            type="file"
            ref={inputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
          />
          
          <PinIcon 
            onClick={() => !uploading && inputRef.current?.click()} 
            className={uploading ? 'opacity-50' : 'cursor-pointer'}
          />

          <Button 
            variant='secondary' 
            type='submit' 
            disabled={!canSend || loading}
          >
            {uploading ? (
              <>
                <LoaderIcon width={16} className="me-2" />
                Sending…
              </>
            ) : loading ? (
              'Sending…'
            ) : (
              'Send'
            )}
          </Button>
        </Form.Group>
      </Form>
    </div>
  );
};


